import { useEffect, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { getTelegramWebApp } from '../utils/telegram';
import { backLayerCount, dismissBackLayer, subscribeBackLayers } from '../utils/backLayers';
import { observeTelegramViewport } from '../utils/telegramViewport';
import { unsavedCount, subscribeUnsaved } from '../utils/unsavedChanges';
import { useTranslation } from 'react-i18next';

export default function TelegramBridge() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const layers = useSyncExternalStore(subscribeBackLayers, backLayerCount, backLayerCount);
  const drafts = useSyncExternalStore(subscribeUnsaved, unsavedCount, unsavedCount);
  useEffect(() => {
    const app = getTelegramWebApp();
    if (app?.isVersionAtLeast?.('6.2')) {
      if (drafts) app.enableClosingConfirmation();
      else app.disableClosingConfirmation();
    }
    const beforeUnload = event => { event.preventDefault(); event.returnValue = ''; };
    if (drafts) window.addEventListener('beforeunload', beforeUnload);
    return () => { window.removeEventListener('beforeunload', beforeUnload); };
  }, [drafts]);

  useEffect(() => {
    const app = getTelegramWebApp();
    if (app) return observeTelegramViewport(app);
  }, []);

  useEffect(() => {
    const app = getTelegramWebApp();
    if (!app?.BackButton) return;
    const back = () => {
      if (dismissBackLayer()) return;
      if (document.documentElement.dataset.keyboard === 'true') { document.activeElement?.blur(); return; }
      const leave = () => window.history.state?.idx > 0 ? navigate(-1) : navigate('/');
      if (drafts && app.isVersionAtLeast?.('6.2')) app.showConfirm(t('telegram.discardChanges'), confirmed => { if (confirmed) leave(); });
      else leave();
    };
    app.BackButton.onClick(back);
    if (!layers && ['/', '/login', '/register'].includes(pathname)) app.BackButton.hide();
    else app.BackButton.show();
    return () => { app.BackButton.offClick(back); app.BackButton.hide(); };
  }, [pathname, navigate, layers, drafts, t]);

  useEffect(() => {
    const app = getTelegramWebApp();
    if (!app) return;
    app.setHeaderColor(colors.background);
    app.setBackgroundColor(colors.background);
    if (app.isVersionAtLeast('7.10')) app.setBottomBarColor(colors.background);
  }, [colors.background]);

  return null;
}
